/**
 * Pagina dettaglio articolo di magazzino.
 *
 * Mostra i dati dell'articolo con fornitore, posizione, data acquisto,
 * lotto più recente, e pulsante stampa etichetta.
 *
 * Tab:
 * - Lotti: tabella lotti associati all'articolo
 * - Movimenti: storico movimenti dell'articolo
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormatters } from '@lib/formatters';
import LottiTable from '../components/LottiTable';
import MovimentiTable from '../components/MovimentiTable';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ArticoloData {
  id: string;
  sku_interno: string;
  descrizione: string | null;
  fornitore_preferenziale_id: string;
  posizione_default_id: string | null;
  metodo_uscita: string;
  soglia_riordino: number | null;
  soglia_minima: number | null;
  descrizione_estesa: string | null;
  consigliato_per: string | null;
  giacenza: number;
  stato: string;
  note: string | null;
  created_at: string;
  updated_at: string;
}

interface LottoData {
  id: string;
  numero_lotto: string | null;
  quantita_disponibile: number;
  quantita_iniziale: number;
  data_ingresso: string;
  data_scadenza: string | null;
  fornitore_id: string | null;
  posizione_id: string | null;
  esito_verifica: string;
  stato: string;
  costo_unitario: number | null;
}

const AUTH_HEADERS = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ArticoloDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fmt = useFormatters();
  const printRef = useRef<HTMLDivElement>(null);

  const [articolo, setArticolo] = useState<ArticoloData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dati risolti
  const [fornitoreNome, setFornitoreNome] = useState<string>('');
  const [posizioneNome, setPosizioneNome] = useState<string>('');
  const [ultimoLotto, setUltimoLotto] = useState<LottoData | null>(null);

  // -----------------------------------------------------------------------
  // Load data
  // -----------------------------------------------------------------------

  const caricaArticolo = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/v1/magazzino/articoli/${id}`, {
        headers: AUTH_HEADERS(),
      });
      if (!response.ok) throw new Error(`Errore ${response.status}`);
      const data: ArticoloData = await response.json();
      setArticolo(data);

      // Risolvi nome fornitore
      if (data.fornitore_preferenziale_id) {
        const fResp = await fetch(
          `/api/v1/soggetti/${data.fornitore_preferenziale_id}`,
          { headers: AUTH_HEADERS() },
        );
        if (fResp.ok) {
          const fData = await fResp.json();
          setFornitoreNome(fData.ragione_sociale ?? fData.nome ?? '');
        }
      }

      // Risolvi nome posizione
      if (data.posizione_default_id) {
        const pResp = await fetch(
          `/api/v1/magazzino/posizioni/${data.posizione_default_id}`,
          { headers: AUTH_HEADERS() },
        );
        if (pResp.ok) {
          const pData = await pResp.json();
          setPosizioneNome(`${pData.codice} — ${pData.descrizione}`);
        }
      }

      // Carica ultimo lotto (il più recente per data_ingresso)
      const lottiResp = await fetch(
        `/api/v1/magazzino/lotti?articolo_id=${id}&limit=1`,
        { headers: AUTH_HEADERS() },
      );
      if (lottiResp.ok) {
        const lotti: LottoData[] = await lottiResp.json();
        if (lotti.length > 0) {
          setUltimoLotto(lotti[0]);

          // Se l'articolo non ha posizione default, usa quella del lotto
          if (!data.posizione_default_id && lotti[0].posizione_id) {
            const pResp2 = await fetch(
              `/api/v1/magazzino/posizioni/${lotti[0].posizione_id}`,
              { headers: AUTH_HEADERS() },
            );
            if (pResp2.ok) {
              const pData2 = await pResp2.json();
              setPosizioneNome(`${pData2.codice} — ${pData2.descrizione}`);
            }
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void caricaArticolo();
  }, [caricaArticolo]);

  // -----------------------------------------------------------------------
  // Print label
  // -----------------------------------------------------------------------

  const handleStampaEtichetta = () => {
    if (!printRef.current) return;
    const printWindow = window.open('', '_blank', 'width=400,height=300');
    if (!printWindow) return;

    const lottoInfo = ultimoLotto;
    const scadenzaStr = lottoInfo?.data_scadenza
      ? fmt.date(lottoInfo.data_scadenza)
      : 'N/D';
    const ingressoStr = lottoInfo?.data_ingresso
      ? fmt.date(lottoInfo.data_ingresso)
      : 'N/D';

    printWindow.document.write(`<!DOCTYPE html>
<html><head><title>Etichetta ${articolo?.sku_interno}</title>
<style>
  @page { size: 60mm 40mm; margin: 2mm; }
  body { font-family: 'Inter', Arial, sans-serif; font-size: 9pt; margin: 0; padding: 4mm; }
  .sku { font-size: 16pt; font-weight: 700; font-family: 'Courier New', monospace; letter-spacing: 1px; margin-bottom: 2mm; }
  .desc { font-size: 9pt; margin-bottom: 2mm; font-weight: 500; }
  .info { font-size: 7pt; color: #555; line-height: 1.4; }
  .info span { font-weight: 600; color: #000; }
  .barcode { font-family: 'Courier New', monospace; font-size: 14pt; letter-spacing: 3px; text-align: center; padding: 2mm 0; border-top: 1px solid #ccc; border-bottom: 1px solid #ccc; margin: 2mm 0; }
  hr { border: none; border-top: 0.5px solid #ddd; margin: 2mm 0; }
</style></head><body>
  <div class="sku">${articolo?.sku_interno ?? ''}</div>
  <div class="desc">${articolo?.descrizione ?? ''}</div>
  <div class="barcode">${articolo?.sku_interno ?? ''}</div>
  <div class="info">
    <div>Lotto: <span>${lottoInfo?.numero_lotto ?? 'N/D'}</span></div>
    <div>Scadenza: <span>${scadenzaStr}</span> | Ingresso: <span>${ingressoStr}</span></div>
    <div>Posizione: <span>${posizioneNome || 'N/D'}</span></div>
    <div>Fornitore: <span>${fornitoreNome || 'N/D'}</span></div>
  </div>
</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  if (loading) {
    return (
      <p className="p-6 text-center text-sm text-muted-foreground">
        Caricamento…
      </p>
    );
  }

  if (error || !articolo) {
    return (
      <p className="p-6 text-center text-sm text-destructive">
        {error ?? 'Articolo non trovato'}
      </p>
    );
  }

  return (
    <div className="space-y-6 p-6" ref={printRef}>
      {/* Intestazione */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          ← Indietro
        </Button>
        <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
          {articolo.sku_interno}
        </h1>
        {articolo.descrizione && (
          <span className="text-muted-foreground">— {articolo.descrizione}</span>
        )}
        <Badge
          variant={articolo.stato === 'attivo' ? 'default' : 'secondary'}
        >
          {articolo.stato === 'attivo' ? 'Attivo' : 'Disattivato'}
        </Badge>
        <div className="ml-auto flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleStampaEtichetta}
          >
            🏷 Stampa etichetta
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(`/magazzino/articoli/${articolo.id}/modifica`)}
          >
            Modifica
          </Button>
        </div>
      </div>

      {/* Dettagli articolo */}
      <Card>
        <CardHeader>
          <CardTitle>Dettagli articolo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-4">
            <div>
              <p className="text-muted-foreground">Fornitore</p>
              <p className="font-medium">
                {fornitoreNome || '—'}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Posizione</p>
              <p className="font-medium">
                {posizioneNome || '—'}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Metodo uscita</p>
              <p className="font-medium">
                {articolo.metodo_uscita.toUpperCase()}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Giacenza</p>
              <p className="font-mono text-lg font-semibold">
                {articolo.giacenza}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Soglia riordino</p>
              <p className="font-mono">
                {articolo.soglia_riordino != null
                  ? articolo.soglia_riordino
                  : '—'}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Soglia minima</p>
              <p className="font-mono">
                {articolo.soglia_minima != null
                  ? articolo.soglia_minima
                  : '—'}
              </p>
            </div>
            {ultimoLotto && (
              <>
                <div>
                  <p className="text-muted-foreground">Ultimo acquisto</p>
                  <p className="font-medium">
                    {fmt.date(ultimoLotto.data_ingresso)}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Ultimo lotto</p>
                  <p className="font-mono">
                    {ultimoLotto.numero_lotto ?? '—'}
                  </p>
                </div>
              </>
            )}
          </div>
          {articolo.note && (
            <div className="mt-4">
              <p className="text-muted-foreground">Note</p>
              <p className="text-sm">{articolo.note}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Scheda prodotto */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Descrizione prodotto</CardTitle>
          </CardHeader>
          <CardContent>
            {articolo.descrizione_estesa ? (
              <p className="whitespace-pre-line text-sm leading-relaxed">
                {articolo.descrizione_estesa}
              </p>
            ) : (
              <p className="text-sm italic text-muted-foreground">
                Nessuna descrizione — clicca Modifica per compilare
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Consigliato per</CardTitle>
          </CardHeader>
          <CardContent>
            {articolo.consigliato_per ? (
              <p className="whitespace-pre-line text-sm leading-relaxed">
                {articolo.consigliato_per}
              </p>
            ) : (
              <p className="text-sm italic text-muted-foreground">
                Nessuna indicazione — clicca Modifica per compilare
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tab lotti / movimenti */}
      <Tabs defaultValue="lotti">
        <TabsList>
          <TabsTrigger value="lotti">Lotti</TabsTrigger>
          <TabsTrigger value="movimenti">Movimenti</TabsTrigger>
        </TabsList>
        <TabsContent value="lotti" className="mt-4">
          <LottiTable articoloId={articolo.id} />
        </TabsContent>
        <TabsContent value="movimenti" className="mt-4">
          <MovimentiTable articoloId={articolo.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ArticoloDetail;
